import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack as faThumbtackSolid } from '@fortawesome/pro-solid-svg-icons/faThumbtack';
import { faThumbtack as faThumbtackLight } from '@fortawesome/pro-light-svg-icons/faThumbtack';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Textarea } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import messages from './messages';

import style from './style.module.less';

const NoteForType = ({
  className,
  labelClassName,
  noteBody,
  noteType,
  updateNote,
  pinned = false,
  updatePinnedNotes: updatePinnedNotesProp,
}) => {
  const [noteText, setNoteText] = useState(noteBody);

  const hasContent = useMemo(() => {
    return !!noteText?.trim();
  }, [noteText]);

  useEffect(() => {
    setNoteText(noteBody);
  }, [noteBody, setNoteText]);

  const onChange = useCallback(
    (e) => {
      setNoteText(e.target.value);
    },
    [setNoteText],
  );

  const saveNote = useCallback(() => {
    updateNote({ type: noteType.id, body: noteText });
  }, [noteType.id, noteText, updateNote]);

  const onBlur = useCallback(
    (e) => {
      if (e.target.value === noteBody) {
        return;
      }
      saveNote();
    },
    [noteBody, saveNote],
  );

  const updatePinnedNotes = useCallback(() => {
    updatePinnedNotesProp(noteType.id);
  }, [noteType.id, updatePinnedNotesProp]);

  const buttonLabel = pinned ? (
    <FormattedMessage {...messages.unpinNote} values={{ noteType: noteType.message }} />
  ) : (
    <FormattedMessage {...messages.pinNote} values={{ noteType: noteType.message }} />
  );

  const buttonPendoLabel = `/order-details#updatePinnedNotes/${pinned ? 'Unpin' : 'Pin'}/${noteType.id}`;

  return (
    <tr key={noteType.id} className={classnames(style['fulfillment-plan-note-container'], className)}>
      <td className={style['note-type-label']}>
        <label className={labelClassName} htmlFor={noteType.id}>
          {noteType.message}
        </label>
      </td>
      <td className={style['note-input']}>
        <Textarea
          autosize
          classNames={{ input: classnames(style['note-textarea'], { [style['has-content']]: hasContent }) }}
          id={noteType.id}
          onBlur={onBlur}
          onChange={onChange}
          value={noteText}
        />
      </td>
      <td className={classnames(style['note-action'])}>
        <Tooltip openDelayMs={500}>
          <Button
            aria-label={buttonLabel}
            className={classnames(style['pin-button'], {
              [style.pinned]: pinned,
            })}
            onClick={updatePinnedNotes}
            pendoTrackingId={buttonPendoLabel}
            theme='link'
          >
            {pinned ? (
              <FontAwesomeIcon className={style['pin-icon']} icon={faThumbtackSolid} />
            ) : (
              <FontAwesomeIcon className={style['pin-icon']} icon={faThumbtackLight} />
            )}
          </Button>
          <TooltipContent>
            {pinned ? <FormattedMessage {...messages.unpinTooltip} /> : <FormattedMessage {...messages.pinTooltip} />}
          </TooltipContent>
        </Tooltip>
      </td>
    </tr>
  );
};

NoteForType.propTypes = {
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  noteType: PropTypes.object.isRequired,
  noteBody: PropTypes.string.isRequired,
  updateNote: PropTypes.func.isRequired,
  pinned: PropTypes.bool,
  updatePinnedNotes: PropTypes.func.isRequired,
};

export default NoteForType;
